import Immutable from 'immutable';

import {
  FETCH_USER_SUBSCRIPTION_PLANS_START,
  FETCH_USER_SUBSCRIPTION_PLANS_SUCCESS,
  FETCH_USER_SUBSCRIPTION_PLANS_FAILURE,
  CANCEL_USER_SUBSCRIPTION_PLAN_START,
  CANCEL_USER_SUBSCRIPTION_PLAN_SUCCESS,
  CANCEL_USER_SUBSCRIPTION_PLAN_FAILURE,
  FETCH_OWNER_AND_ADMINS_START,
  FETCH_OWNER_AND_ADMINS_SUCCESS,
  INVITE_USER_TO_SUBSCRIPTION,
  FETCH_PAYMENT_METHOD_SUMMARY,
  FETCH_PAYMENT_METHOD_SUMMARY_SUCCESS,
  REVOKE_USER_ACCESS_TO_SUBSCRIPTION,
  REVOKE_USER_ACCESS_TO_SUBSCRIPTION_SUCCESS,
  FETCH_USER_SUBSCRIPTION_PLAN_DATA,
  FETCH_USER_SUBSCRIPTION_PLAN_DATA_SUCCESS,
  FETCH_DAILY_USAGE_SUMMARY,
  SET_CURRENT_SELECTED_USER_SUBSCRIPTION,
  REDIRECT_TO_SOLUTIONS_PAGE,
  ADD_ADMIN_TO_INSTITUTION,
  ADD_ADMIN_TO_INSTITUTION_SUCCESS,
  ADD_ADMIN_TO_INSTITUTION_FAILURE,
  SETUP_CURRENT_SELECTED_PLAN,
  LIST_INSTITUTIONS_UNDER_PLAN,
  LIST_INSTITUTIONS_UNDER_PLAN_FAILURE,
  LIST_INSTITUTIONS_UNDER_PLAN_SUCCESS,
  GET_STRIPE_CONNECT_LINK,
  GET_STRIPE_CONNECT_LINK_ERROR,
  SET_UP_STRIPE_CONNECT,
  SET_UP_STRIPE_CONNECT_ERROR,
  REDIRECT_TO_STRIPE_CONNECT_PAGE,
} from './actions';

import Resource, { ResourceApi } from '../../resource/Resource';
import {
  USER_SUBSCRIPTION_PLAN as USER_SUBSCRIPTION_PLAN_RESOURCE,
  USER_RESOURCE,
  SUBSCRIPTION_ROLE,
  LEARNER_USAGE,
  INSTITUTION_RESOURCE,
} from '../resourceNames';
import { currentUserIdSelector } from '../Auth/selectors';
import {
  selectCurrentUserSubscriptionPlansHasLoaded,
  selectCurrentUserSubscriptionPlans,
  selectCurrentUserSubscriptionPlanSummaries,
  selectCurrentSelectedUserSubscription,
} from './selectors';
import {
  ROUTE_ACCOUNTS_SUBSCRIPTIONS,
  ROUTE_PAYMENT_UPDATE_PAYMENT_METHOD_PAGE,
} from '../routeActions';
import { fetchCurrentUser } from '../Users/dispatchers.common';
import {
  selectQueryObject,
  selectQuery,
  selectPayload,
} from '../location/selectors.web';

export const updateQueryParams = (query) => (dispatch, getState) => {
  const oldQuery = selectQuery(getState());
  const params = selectPayload(getState()).toJS();
  dispatch({
    type: ROUTE_ACCOUNTS_SUBSCRIPTIONS,
    params,
    query: oldQuery.merge(Immutable.fromJS(query)).toJS(),
  });
};

export const setManageSubscriptionView = (view) => (dispatch) => {
  dispatch(
    updateQueryParams({
      view,
    })
  );
};

export const setCurrentUserSubscriptionPlan = (userSubscriptionPlan) => (
  dispatch,
  getState
) => {
  const state = getState();
  const userId = currentUserIdSelector(state);

  dispatch({
    type: SET_CURRENT_SELECTED_USER_SUBSCRIPTION,
    userSubscriptionPlan,
    userId,
  });

  dispatch(
    updateQueryParams({
      subscriptionId: userSubscriptionPlan.id,
    })
  );
};

export const fetchUserSubscriptionPlanData = (planIds) => (
  dispatch,
  getState
) => {
  const state = getState();
  const userId = currentUserIdSelector(state);

  dispatch({
    type: FETCH_USER_SUBSCRIPTION_PLAN_DATA,
  });

  Promise.all(
    planIds.map((planId) => {
      return ResourceApi.read(USER_SUBSCRIPTION_PLAN_RESOURCE, planId, [
        { method: 'loadManageSubscriptionData', args: {} },
      ]);
    })
  ).then((responses) => {
    const loadManageSubscriptionData = responses.map(([method1]) => method1);
    dispatch({
      type: FETCH_USER_SUBSCRIPTION_PLAN_DATA_SUCCESS,
      loadManageSubscriptionData,
      userId,
    });
  });
};

export const fetchOwnerAndAdmins = () => (dispatch, getState) => {
  const state = getState();
  const userId = currentUserIdSelector(state);
  const subscriptionPlans = selectCurrentUserSubscriptionPlans(state);

  dispatch({
    type: FETCH_OWNER_AND_ADMINS_START,
  });

  const resourceCalls = [];

  subscriptionPlans.forEach((plan) => {
    resourceCalls.push(
      ResourceApi.read(
        USER_SUBSCRIPTION_PLAN_RESOURCE,
        plan.get('id'),
        'getSubscriptionOwnerAndAdmins',
        {}
      )
    );
  });

  Promise.all(resourceCalls).then((data) => {
    dispatch({
      type: FETCH_OWNER_AND_ADMINS_SUCCESS,
      data,
      userId,
    });
  });
};

export const setupCurrentSelectedSubscription = () => (dispatch, getState) => {
  const state = getState();
  const subscriptions = selectCurrentUserSubscriptionPlanSummaries(state);
  const { subscriptionId } = selectQueryObject(state);

  dispatch({
    type: SETUP_CURRENT_SELECTED_PLAN,
  });

  if (subscriptions.length > 0) {
    const defaultSelectedPlan =
      subscriptions.find((plan) => plan.id === subscriptionId) ??
      subscriptions[0];

    dispatch(setCurrentUserSubscriptionPlan(defaultSelectedPlan ?? {}));

    if (!subscriptionId) {
      dispatch(
        updateQueryParams({
          subscriptionId: defaultSelectedPlan.id,
        })
      );
    }
  }
};

export const fetchUserSubscriptionPlans = () => {
  return (dispatch, getState) => {
    const state = getState();
    const hasLoaded = selectCurrentUserSubscriptionPlansHasLoaded(state);
    const userId = currentUserIdSelector(state);

    if (hasLoaded || !userId) {
      return Promise.resolve();
    }

    dispatch({
      type: FETCH_USER_SUBSCRIPTION_PLANS_START,
    });

    return new Promise((resolve, reject) => {
      Resource.lookup(
        USER_SUBSCRIPTION_PLAN_RESOURCE,
        'getUserSubscriptionPlans'
      ).then(
        (subscriptionPlans) => {
          dispatch({
            type: FETCH_USER_SUBSCRIPTION_PLANS_SUCCESS,
            subscriptionPlans,
            userId,
          });

          resolve();
        },
        () => {
          dispatch({
            type: FETCH_USER_SUBSCRIPTION_PLANS_FAILURE,
          });
          reject(Error());
        }
      );
    });
  };
};

export const loadCurrentUserSubscriptionPlanData = (dispatch) => {
  return dispatch(fetchUserSubscriptionPlans());
};

export const cancelUserSubscriptionPlan = (userSubscriptionPlanId) => {
  return (dispatch) => {
    dispatch({
      type: CANCEL_USER_SUBSCRIPTION_PLAN_START,
      userSubscriptionPlanId,
    });

    return ResourceApi.update(
      USER_SUBSCRIPTION_PLAN_RESOURCE,
      userSubscriptionPlanId,
      'scheduleCancelSubscription'
    ).then(
      (userSubscriptionPlan) => {
        dispatch({
          type: CANCEL_USER_SUBSCRIPTION_PLAN_SUCCESS,
          userSubscriptionPlan,
        });
      },
      (error) => {
        dispatch({
          type: CANCEL_USER_SUBSCRIPTION_PLAN_FAILURE,
        });
        throw error;
      }
    );
  };
};

export const updatePaymentMethodClick = (userSubscriptionPlanId) => {
  return (dispatch) => {
    dispatch({
      type: ROUTE_PAYMENT_UPDATE_PAYMENT_METHOD_PAGE,
      query: {
        subscriptionId: userSubscriptionPlanId,
      },
    });
  };
};

export const fetchPaymentMethod = (userSubscriptionPlanId) => {
  return (dispatch, getState) => {
    const userId = currentUserIdSelector(getState());
    dispatch({
      type: FETCH_PAYMENT_METHOD_SUMMARY,
    });

    ResourceApi.read(
      USER_SUBSCRIPTION_PLAN_RESOURCE,
      userSubscriptionPlanId,
      'getPaymentMethodSummary'
    ).then((summary) => {
      dispatch({
        type: FETCH_PAYMENT_METHOD_SUMMARY_SUCCESS,
        summary,
        userSubscriptionPlanId,
        userId,
      });
    });
  };
};

export const fetchPaymentMethodSummary = (userSubscriptionPlanId) => {
  return () =>
    ResourceApi.read(
      USER_SUBSCRIPTION_PLAN_RESOURCE,
      userSubscriptionPlanId,
      'getPaymentMethodSummary'
    );
};

export const startEducatorTrial = () => {
  return (dispatch, getState) => {
    const userId = currentUserIdSelector(getState());
    return ResourceApi.update(USER_RESOURCE, userId, 'startEducatorTrial').then(
      (result) => {
        if (result === 'started') {
          return dispatch(fetchCurrentUser());
        }
        throw new Error(result);
      }
    );
  };
};

export const manageUserSubscriptionPlan = () => {
  return async () => {
    const result = await ResourceApi.lookup(
      USER_SUBSCRIPTION_PLAN_RESOURCE,
      'getStripeCustomerPortalUrl',
      {
        returnUrl: window.location.href, // Return back to current page
      }
    );
    window.location.href = result; // Redirect to Stripe customer portal
  };
};

export const fetchDailyUsageData = (subscriptionId, fromDate, toDate) => (
  dispatch,
  getState
) => {
  const state = getState();
  const userId = currentUserIdSelector(state);

  ResourceApi.read(LEARNER_USAGE, subscriptionId, [
    {
      method: 'getDailyUsageSummary',
      args: {
        fromDate,
        toDate,
      },
    },
  ]).then(
    ({
      /* eslint camelcase: ["error", {allow: ["subscription_id", "daily_usages"]}] */
      subscription_id,
      daily_usages,
    }) => {
      dispatch({
        type: FETCH_DAILY_USAGE_SUMMARY,
        userId,
        subscriptionId: subscription_id,
        dailyUsages: daily_usages,
      });
    }
  );
};

export const revokeUserAccessToSubscription = (subscriptionRoleId) => (
  dispatch
) => {
  dispatch({
    type: REVOKE_USER_ACCESS_TO_SUBSCRIPTION,
  });

  ResourceApi.delete(SUBSCRIPTION_ROLE, subscriptionRoleId).then(() => {
    dispatch({
      type: REVOKE_USER_ACCESS_TO_SUBSCRIPTION_SUCCESS,
    });
    dispatch(fetchOwnerAndAdmins());
  });
};

export const inviteUserToSubscription = (userId, subscriptionId) => (
  dispatch
) => {
  dispatch({
    type: INVITE_USER_TO_SUBSCRIPTION,
  });

  ResourceApi.create(
    SUBSCRIPTION_ROLE,
    {
      userId,
      roleType: 'admin',
      subscriptionId,
    },
    []
  ).then(() => {
    dispatch(fetchOwnerAndAdmins());
  });
};

export const redirectToStripeConnectPage = (url) => (dispatch) => {
  dispatch({
    type: REDIRECT_TO_STRIPE_CONNECT_PAGE,
  });
  // TODO: consider alternate way of sending user to page. Not positive we should pass
  //       query params to url like this
  window.location.href = url;
};

export const redirectToSolutionsPage = (subscriptionId) => (dispatch) => {
  dispatch({
    type: REDIRECT_TO_SOLUTIONS_PAGE,
  });
  // TODO: consider alternate way of sending user to page. Not positive we should pass
  //       query params to url like this
  window.location.href = `https://solutions.openlearning.com/pricing?subscriptionId=${subscriptionId}`;
};

export const listInstitutionsUnderPlan = (subscriptionId) => async (
  dispatch,
  getState
) => {
  const state = getState();
  const userId = currentUserIdSelector(state);
  dispatch({
    type: LIST_INSTITUTIONS_UNDER_PLAN,
  });

  try {
    const [institutions] = await ResourceApi.read(
      USER_SUBSCRIPTION_PLAN_RESOURCE,
      subscriptionId,
      [
        {
          method: 'listInstitutionsUnderPlan',
          args: {},
        },
      ]
    );

    dispatch({
      type: LIST_INSTITUTIONS_UNDER_PLAN_SUCCESS,
      institutions,
      subscriptionId,
      userId,
    });
  } catch (error) {
    dispatch({
      type: LIST_INSTITUTIONS_UNDER_PLAN_FAILURE,
      error,
    });
  }

  return Promise.resolve();
};

export const addAdmin = (institutionId, userId) => async (
  dispatch,
  getState
) => {
  const state = getState();
  const currentUserId = currentUserIdSelector(state);
  const subscription = selectCurrentSelectedUserSubscription(state);

  dispatch({
    type: ADD_ADMIN_TO_INSTITUTION,
  });

  try {
    await ResourceApi.update(INSTITUTION_RESOURCE, institutionId, [
      {
        method: 'addAdmin',
        args: {
          actioningUserId: currentUserId,
          userId,
        },
      },
    ]);
    dispatch({
      type: ADD_ADMIN_TO_INSTITUTION_SUCCESS,
    });

    if (subscription) {
      return dispatch(listInstitutionsUnderPlan(subscription.id));
    }
  } catch (error) {
    dispatch({
      type: ADD_ADMIN_TO_INSTITUTION_FAILURE,
    });
  }

  return Promise.resolve();
};

export const setUpStripeConnect = (country) => async (dispatch, getState) => {
  const state = getState();
  const subscription = selectCurrentSelectedUserSubscription(state);

  dispatch({
    type: SET_UP_STRIPE_CONNECT,
  });

  try {
    const accountLink = await ResourceApi.update(
      USER_SUBSCRIPTION_PLAN_RESOURCE,
      subscription?.id,
      [
        {
          method: 'createStripeConnectAccount',
          args: {
            country,
          },
        },
      ]
    );
    dispatch(redirectToStripeConnectPage(accountLink));
  } catch (error) {
    dispatch({
      type: SET_UP_STRIPE_CONNECT_ERROR,
    });
  }
};

export const goToStripeConnect = () => async (dispatch, getState) => {
  const state = getState();
  const subscription = selectCurrentSelectedUserSubscription(state);

  dispatch({
    type: GET_STRIPE_CONNECT_LINK,
    subscriptionId: subscription?.id,
  });

  try {
    const link = await ResourceApi.read(
      USER_SUBSCRIPTION_PLAN_RESOURCE,
      subscription?.id,
      [
        {
          method: 'getStripeConnectLink',
          args: {},
        },
      ]
    );

    dispatch(redirectToStripeConnectPage(link));
  } catch (error) {
    dispatch({
      type: GET_STRIPE_CONNECT_LINK_ERROR,
      subscriptionId: subscription?.id,
      error,
    });
  }
};
